// Action failures — the one channel a REJECTED mutation reports through
// (TMF-362).
//
// Gate actions (approve/reject a review, resolve a match exception, mark a task
// done) were optimistic with a rollback-only onError: the server's refusal
// looked exactly like a broken button, which is how a roleless user's approval
// in production "did nothing". Every such mutation now calls
// reportActionError, and the app shell renders the message as the danger
// variant of the existing toast (components/ActionErrorFlash).
//
// Module-level store, mirroring lib/realtime/signalBus's subscribe idiom: the
// reporters are mutation callbacks scattered across routes and hooks, the
// consumer is one component mounted in the authenticated shell, and neither
// should have to thread the other through props.
//
// The failures live in the STORE, not in the renderer's state. The shell
// unmounts ActionErrorFlash while a focused thread is open (DESIGN.md's
// no-interruption rule), so a rejection reported in that window would reach
// nobody if the only stateholder were the subscriber. Reports made with no
// renderer mounted are buffered and render the moment one mounts (TMF-399) —
// coherent because these never auto-dismiss: a rejection is the answer to
// something the user did, so it waits for them.

import { useSyncExternalStore } from "react";

import { formatValidationError, isValidationError } from "~/lib/cases";

/** One surfaced failure. `raisedAt` is exposed so the renderer can stable-sort
 *  or animate without re-deriving from a separate clock. */
export interface ActionError {
  id: string;
  message: string;
  raisedAt: number;
}

/** What the user is told when the failure is NOT caller-facing — a 5XX, a
 *  network drop, a thrown non-HalError. Those bodies can carry internal
 *  detail, so they are never rendered (the same 4XX/5XX split the API's
 *  services draw). */
const OPAQUE_ACTION_FAILURE =
  "Something went wrong — that didn't go through. Please try again.";

/** At most this many failures are kept; older ones drop off the stack. */
const MAX_VISIBLE = 5;

type Listener = () => void;

const listeners = new Set<Listener>();

let errors: ActionError[] = [];

/**
 * The message to show for a failed action: a 4XX carries the server's own
 * validation detail (that IS the useful sentence — "user_role: required"),
 * anything else is opaque.
 */
function actionErrorMessage(err: unknown): string {
  if (isValidationError(err)) return formatValidationError(err);
  return OPAQUE_ACTION_FAILURE;
}

function notify(): void {
  for (const fn of listeners) fn();
}

/** The buffer itself. `snapshot` returns the same array until a push or a
 *  dismiss replaces it, which is what useSyncExternalStore requires. */
export const actionErrorStore = {
  snapshot(): ActionError[] {
    return errors;
  },
  push(error: ActionError): void {
    errors = [...errors, error].slice(-MAX_VISIBLE);
    notify();
  },
  dismiss(id: string): void {
    errors = errors.filter((e) => e.id !== id);
    notify();
  },
  on(fn: Listener): () => void {
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  },
};

/**
 * Report a failed action for display. Call from a mutation's onError — keep
 * whatever cache rollback that handler already does; this only adds the part
 * the user can see.
 */
export function reportActionError(err: unknown): void {
  const raisedAt = Date.now();
  actionErrorStore.push({
    id: `${raisedAt}-${Math.random().toString(36).slice(2, 8)}`,
    message: actionErrorMessage(err),
    raisedAt,
  });
}

/**
 * Read the buffered failures. Deliberately no auto-dismiss (unlike the
 * informational flashes): a rejection is the answer to something the user just
 * did, so it stays until dismissed rather than expiring while they read it.
 */
export function useActionErrors(): {
  errors: ActionError[];
  dismiss: (id: string) => void;
} {
  return {
    errors: useSyncExternalStore(actionErrorStore.on, actionErrorStore.snapshot),
    dismiss: actionErrorStore.dismiss,
  };
}
